(() => {
  const namespaceKey = 'GuruShopMinGuruSessionStatus';
  const markerSelector = 'script[data-min-guru-session-root]';
  const accountSelector = '[data-min-guru-account]';
  const accountShellSelector = '[data-min-guru-account-shell]';
  const accountMenuSelector = '[data-min-guru-account-menu]';
  const authenticatedOnlySelector = '[data-min-guru-authenticated-only]';
  const signedOutOnlySelector = '[data-min-guru-signed-out-only]';
  const existingApi = window[namespaceKey];

  if (existingApi?.initById) {
    document.querySelectorAll(markerSelector).forEach((marker) => {
      existingApi.initById(marker.dataset.minGuruSessionRoot);
    });
    return;
  }

  const rootBindings = new WeakMap();
  const requestByEndpoint = new Map();
  const sessionByEndpoint = new Map();
  const rootsByEndpoint = new Map();
  const sessionFreshnessMs = 60_000;
  const requestTimeoutMs = 4_000;

  const sessionEndpoint = (accountLink) => {
    try {
      const endpoint = new URL(accountLink.href);
      endpoint.pathname = '/min-guru/api/session';
      endpoint.search = '';
      endpoint.hash = '';
      return endpoint.toString();
    } catch {
      return null;
    }
  };

  const readSession = (endpoint, { force = false } = {}) => {
    const cached = sessionByEndpoint.get(endpoint);
    if (!force && cached && Date.now() - cached.confirmedAt < sessionFreshnessMs) {
      return Promise.resolve(cached.session);
    }
    if (!requestByEndpoint.has(endpoint)) {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), requestTimeoutMs);
      const request = fetch(endpoint, {
        credentials: 'include',
        headers: { Accept: 'application/json' },
        method: 'GET',
        signal: controller.signal,
      })
        .then(async (response) => {
          const session = await response.json().catch(() => null);
          const state = session?.state || (
            typeof session?.authenticated === 'boolean'
              ? session.authenticated ? 'authenticated' : 'anonymous'
              : null
          );
          if (
            state === 'authenticated' &&
            session.authenticated === true
          ) {
            session.state = state;
            sessionByEndpoint.set(endpoint, {
              confirmedAt: Date.now(),
              session,
            });
            return session;
          }
          if (
            response.ok &&
            state === 'anonymous' &&
            session.authenticated === false
          ) {
            session.state = state;
            sessionByEndpoint.set(endpoint, {
              confirmedAt: Date.now(),
              session,
            });
            return session;
          }
          return { authenticated: false, state: 'unavailable' };
        })
        .catch(() => ({ authenticated: false, state: 'unavailable' }))
        .finally(() => {
          window.clearTimeout(timeout);
          requestByEndpoint.delete(endpoint);
        });

      requestByEndpoint.set(endpoint, request);
    }

    return requestByEndpoint.get(endpoint);
  };

  const applyEndpointSession = (endpoint, session) => {
    if (session?.state === 'unavailable') return;
    rootsByEndpoint.get(endpoint)?.forEach((sectionRoot) => {
      const binding = rootBindings.get(sectionRoot);
      if (binding?.endpoint !== endpoint) return;
      applySession(sectionRoot, session);
    });
    document.dispatchEvent(new CustomEvent('min-guru:session', {
      detail: {
        authenticated: session?.authenticated === true,
        member: session?.member === true,
        state: session?.state || 'anonymous',
      },
    }));
  };

  const refreshEndpoint = (endpoint, force = true) =>
    readSession(endpoint, { force }).then((session) => {
      applyEndpointSession(endpoint, session);
      return session;
    });

  const refreshAll = () => {
    rootsByEndpoint.forEach((_roots, endpoint) => {
      void refreshEndpoint(endpoint);
    });
  };

  const restoreSignedOut = (sectionRoot) => {
    delete sectionRoot.dataset.minGuruAuthenticated;

    sectionRoot.querySelectorAll(accountSelector).forEach((accountLink) => {
      const defaultLabel = accountLink.dataset.minGuruDefaultLabel || 'Min Guru';
      const accountShell = accountLink.closest(accountShellSelector);
      const accountMenu = accountShell?.querySelector(accountMenuSelector);
      const accountTrigger = accountShell?.querySelector('[data-min-guru-account-trigger]');

      delete accountLink.dataset.minGuruAuthenticated;
      accountLink.hidden = false;
      accountLink.setAttribute('aria-label', defaultLabel);
      accountShell?.removeAttribute('data-min-guru-authenticated');
      accountShell?.querySelectorAll('[data-min-guru-account-label]').forEach((label) => {
        label.textContent = defaultLabel;
      });

      if (accountMenu instanceof HTMLDetailsElement) {
        accountMenu.open = false;
        accountMenu.hidden = true;
        accountTrigger?.setAttribute('aria-label', defaultLabel);
      }
    });

    sectionRoot.querySelectorAll('[data-min-guru-account-greeting]').forEach((label) => {
      label.textContent = label.dataset.minGuruDefaultLabel || 'Din konto';
    });
    sectionRoot.querySelectorAll(signedOutOnlySelector).forEach((element) => {
      element.hidden = false;
    });
    sectionRoot.querySelectorAll(authenticatedOnlySelector).forEach((element) => {
      element.hidden = true;
    });
  };

  const applySession = (sectionRoot, session) => {
    restoreSignedOut(sectionRoot);
    if (!session?.authenticated || typeof session.firstName !== 'string') return;

    const firstName = session.firstName.trim().slice(0, 60);
    if (!firstName) return;

    sectionRoot.dataset.minGuruAuthenticated = 'true';
    sectionRoot.querySelectorAll(accountSelector).forEach((accountLink) => {
      const accountShell = accountLink.closest(accountShellSelector);
      const accountMenu = accountShell?.querySelector(accountMenuSelector);
      const accountTrigger = accountShell?.querySelector('[data-min-guru-account-trigger]');

      accountLink.dataset.minGuruAuthenticated = 'true';
      accountShell?.setAttribute('data-min-guru-authenticated', 'true');
      accountLink.setAttribute('aria-label', `Åbn Min Guru for ${firstName}`);
      accountShell?.querySelectorAll('[data-min-guru-account-label]').forEach((label) => {
        label.textContent = `Hej, ${firstName}`;
      });

      if (accountMenu instanceof HTMLDetailsElement) {
        accountLink.hidden = true;
        accountMenu.hidden = false;
        accountTrigger?.setAttribute('aria-label', `Åbn konto-menu for ${firstName}`);
      }
    });
    sectionRoot.querySelectorAll('[data-min-guru-account-greeting]').forEach((label) => {
      label.textContent = `Hej, ${firstName}`;
    });
    sectionRoot.querySelectorAll(signedOutOnlySelector).forEach((element) => {
      element.hidden = true;
    });
    sectionRoot.querySelectorAll(authenticatedOnlySelector).forEach((element) => {
      element.hidden = false;
    });
  };

  const initRoot = (sectionRoot) => {
    if (!(sectionRoot instanceof HTMLElement) || rootBindings.has(sectionRoot)) return false;

    const accountLinks = Array.from(sectionRoot.querySelectorAll(accountSelector));
    if (accountLinks.length === 0) return false;
    restoreSignedOut(sectionRoot);

    const closeMenus = (event) => {
      if (event.type === 'keydown' && event.key !== 'Escape') return;

      const openMenus = sectionRoot.querySelectorAll(`${accountMenuSelector}[open]`);

      openMenus.forEach((menu) => {
        if (event.type === 'mousedown' && menu.contains(event.target)) return;
        menu.open = false;

        if (event.type === 'keydown' && event.key === 'Escape') {
          menu.querySelector('summary')?.focus();
        }
      });
    };

    document.addEventListener('mousedown', closeMenus);
    document.addEventListener('keydown', closeMenus);
    const endpoint = accountLinks.map(sessionEndpoint).find(Boolean);
    rootBindings.set(sectionRoot, { closeMenus, endpoint });
    sectionRoot.dataset.minGuruSessionBound = 'true';
    if (endpoint) {
      const roots = rootsByEndpoint.get(endpoint) || new Set();
      roots.add(sectionRoot);
      rootsByEndpoint.set(endpoint, roots);
      readSession(endpoint).then((session) => applyEndpointSession(endpoint, session));
    }

    return true;
  };

  const cleanupRoot = (sectionRoot) => {
    if (!(sectionRoot instanceof HTMLElement)) return false;
    const binding = rootBindings.get(sectionRoot);
    if (!binding) return false;

    document.removeEventListener('mousedown', binding.closeMenus);
    document.removeEventListener('keydown', binding.closeMenus);
    if (binding.endpoint) {
      const roots = rootsByEndpoint.get(binding.endpoint);
      roots?.delete(sectionRoot);
      if (roots?.size === 0) rootsByEndpoint.delete(binding.endpoint);
    }
    restoreSignedOut(sectionRoot);
    delete sectionRoot.dataset.minGuruSessionBound;
    rootBindings.delete(sectionRoot);
    return true;
  };

  const initById = (rootId) => {
    if (!rootId) return false;
    return initRoot(document.getElementById(rootId));
  };

  document.addEventListener('shopify:section:load', (event) => initRoot(event.target));
  document.addEventListener('shopify:section:unload', (event) => cleanupRoot(event.target));
  window.addEventListener('pageshow', refreshAll);
  window.addEventListener('focus', refreshAll);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refreshAll();
  });

  window[namespaceKey] = {
    cleanupRoot,
    initById,
    initRoot,
    refresh: refreshAll,
    readSession,
  };

  document.querySelectorAll(markerSelector).forEach((marker) => {
    initById(marker.dataset.minGuruSessionRoot);
  });
})();
